import { useEffect, useMemo, useState } from "react";

import { Search } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { ACCOUNT_TYPE_LABELS } from "~/types/contabilidad";

import type { Account } from "~/types/contabilidad";

interface AddAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Eligible accounts (ingreso/gasto) not yet present in the grid
  availableAccounts: Account[];
  onAdd: (accounts: Account[]) => void;
}

export function AddAccountsDialog({
  open,
  onOpenChange,
  availableAccounts,
  onAdd,
}: AddAccountsDialogProps) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedIds(new Set());
    }
  }, [open]);

  const filteredAccounts = useMemo(() => {
    if (!search) return availableAccounts;
    const query = search.toLowerCase();
    return availableAccounts.filter(
      (account) =>
        account.code.toLowerCase().includes(query) ||
        account.name.toLowerCase().includes(query)
    );
  }, [availableAccounts, search]);

  const toggleAccount = (accountId: string) => {
    const next = new Set(selectedIds);
    if (next.has(accountId)) {
      next.delete(accountId);
    } else {
      next.add(accountId);
    }
    setSelectedIds(next);
  };

  const handleAdd = () => {
    const selected = availableAccounts.filter((account) =>
      selectedIds.has(account.id)
    );
    if (selected.length === 0) return;
    onAdd(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Agregar Cuentas</DialogTitle>
          <DialogDescription>
            Selecciona cuentas de ingreso o gasto para presupuestar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por código o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-9"
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto rounded-lg border">
            {filteredAccounts.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                {availableAccounts.length === 0
                  ? "Todas las cuentas elegibles ya están en el presupuesto."
                  : "No se encontraron cuentas."}
              </p>
            ) : (
              filteredAccounts.map((account) => (
                <label
                  key={account.id}
                  className="flex cursor-pointer items-center gap-3 border-b px-4 py-2.5 last:border-b-0 hover:bg-muted/40"
                >
                  <Checkbox
                    checked={selectedIds.has(account.id)}
                    onCheckedChange={() => toggleAccount(account.id)}
                  />
                  <span className="font-mono text-sm text-muted-foreground">
                    {account.code}
                  </span>
                  <span className="flex-1 truncate text-sm">
                    {account.name}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {ACCOUNT_TYPE_LABELS[account.account_type]}
                  </Badge>
                </label>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleAdd} disabled={selectedIds.size === 0}>
            Agregar {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
